/* RecipeCard — visual recipe tile used in the Directory, Home directory peek
 * and CookbookView grids. Shows a 4:3 hero region with image / YouTube poster
 * / palette-gradient fallback, then chips + title + creator/meta below.
 *
 * Variants via `variant` prop:
 *   "default" — used in Directory + Home (shows creator + meta)
 *   "cookbook" — used in CookbookView (shows tags chips + meta, hides creator)
 *
 * Optional admin moderation button overlay is rendered when `onAdminDelete` is
 * passed AND the AdminModerationButtons component decides the user is admin.
 */
import { Link } from "react-router-dom";
import AdminModerationButtons from "@/components/AdminModerationButtons";
import ReportButton from "@/components/ReportButton";
import CopyToCookbookButton from "@/components/CopyToCookbookButton";
import HeartButton from "@/components/HeartButton";
import { recipePath } from "@/lib/slug";
import { SHORT_LABELS } from "@/lib/diets";

export default function RecipeCard({ recipe, variant = "default", onAdminDelete, linkQuery = "", showCopy = false }) {
  const r = recipe || {};
  if (!r.recipe_id) return null;
  // Choose hero image with the same priority as the RecipeView detail page:
  //   1. AI-generated hero (only when the user has toggled it on)
  //   2. First non-empty user-uploaded image
  // If neither exists, we fall through to the branded default in the JSX below.
  // Bug 2026-07-21: previously we only checked r.images, so recipes that had an
  // active ai_image but empty user-uploaded images showed the fallback logo
  // on the Home page and public Directory.
  const heroImage =
    (r.ai_image_active && r.ai_image_url)
      ? r.ai_image_url
      : (r.images || []).find((u) => typeof u === "string" && u.length > 0);
  const cookTotal = (r.prep_minutes || 0) + (r.cook_minutes || 0);

  return (
    <div className="relative">
      {onAdminDelete && (
        <AdminModerationButtons kind="recipe" id={r.recipe_id} label={r.title} onDelete={onAdminDelete} />
      )}
      <ReportButton kind="recipe" id={r.recipe_id} ownerId={r.owner_id} />
      <HeartButton kind="recipe" id={r.recipe_id} count={r.favourite_count} className="absolute top-2 left-2 z-10" />
      <Link
        to={`${recipePath(r)}${linkQuery || ""}`}
        data-testid={`recipe-card-${r.recipe_id}`}
        className="card-recipe hover-lift block !p-0 overflow-hidden h-full flex flex-col"
      >
        {/* Visual region */}
        <div className="relative aspect-[4/3] bg-rf-amber/20 overflow-hidden">
          {heroImage ? (
            <img
              src={heroImage}
              alt={`${r.title}${r.creator_name ? ` — recipe by ${r.creator_name}` : ""}`}
              loading="lazy"
              className="w-full h-full object-cover"
              // Instagram / TikTok / Pinterest signed CDN URLs expire
              // within hours-to-days, leaving a broken image icon on the
              // recipe card. Fall back to the branded default so cards
              // always look intentional (audit 2026-07-19: several
              // shared-from-mobile Instagram recipes were showing broken
              // <img>s in the cookbook grid). The onError also strips
              // the alt-text prefix so the fallback doesn't inherit the
              // "broken image icon" from the browser.
              onError={(e) => {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = "/recipe-default.jpg";
                }
              }}
            />
          ) : (
            <img
              src="/recipe-default.jpg"
              alt={`${r.title} — Recipe Filer branded placeholder`}
              loading="lazy"
              className="w-full h-full object-cover object-center"
              data-testid="recipe-card-default-image"
            />
          )}
        </div>

        {/* Text region */}
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="chip">{r.difficulty || "easy"}</div>
            {r.is_secret && <div className="chip bg-rf-ink/10 text-rf-ink">Secret</div>}
            {variant === "cookbook" && (r.tags || []).slice(0, 2).map((t) => (
              <div key={t} className="chip !bg-rf-cream-alt !text-rf-ink-soft">{t}</div>
            ))}
          </div>
          <h3 className="font-display font-extrabold text-xl text-rf-ink mb-1 line-clamp-2">{r.title}</h3>
          {(r.diet_badges || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2" data-testid={`recipe-card-diets-${r.recipe_id}`}>
              {r.diet_badges.slice(0, 3).map((b) => (
                <span key={b} className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-900 border border-emerald-600/25">{SHORT_LABELS[b] || b}</span>
              ))}
              {r.diet_badges.length > 3 && <span className="text-[10px] font-bold px-1.5 py-0.5 text-rf-ink-soft">+{r.diet_badges.length - 3}</span>}
            </div>
          )}
          {variant === "default" && (
            <p className="text-sm text-rf-ink-soft mb-2">by {r.creator_name || "Anonymous"}</p>
          )}
          <p className="text-sm text-rf-ink-soft mt-auto">
            {cookTotal} min • Serves {r.serves || 4}
            {r.is_public && r.copy_count > 0 && (
              <span data-testid={`recipe-card-copy-count-${r.recipe_id}`}> • Copied {r.copy_count} {r.copy_count === 1 ? "time" : "times"}</span>
            )}
          </p>
        </div>
      </Link>
      {showCopy && r.is_public && (
        <div className="absolute bottom-4 right-4 z-10">
          <CopyToCookbookButton recipe={r} compact className="inline-flex items-center gap-1 rounded-full bg-white border border-rf-ink/15 px-3 py-1.5 text-xs font-bold text-rf-ink hover:bg-rf-amber/20 transition-colors" />
        </div>
      )}
    </div>
  );
}
